import {PlanInfo, INTERVAL_MONTH, BillingInterval} from 'obvio/Billing/plans'
import React from 'react'
import styled from 'styled-components'
import Button from '@material-ui/core/Button'
import {formatDate, LONG_DATE_FORMAT, isPast, getNumDays} from 'lib/date-time'
import {colors} from 'lib/ui/theme'
import PlanActionButton from 'obvio/Billing/PlanCard/PlanActionButton'
import {usePlan} from 'obvio/Billing/PlanProvider'
import {useGetSubscription} from 'obvio/Billing/subscribe'
import {CheckSquare, TimesSquare} from 'lib/ui/Icon'
import {formatPrice} from 'lib/currency'

export default function PlanCard(props: {
  billingInterval: BillingInterval
  plan: PlanInfo
}) {
  const {billingInterval, plan} = props
  const planPrice =
    billingInterval === INTERVAL_MONTH ? plan.priceMonthly : plan.priceYearly

  return (
    <Box>
      <div>
        <Name>{plan.label}</Name>
        <Price>
          <Dollars>${formatPrice(planPrice, {numDecimals: 0})}</Dollars>
          <MonthLabel>/ {billingInterval}</MonthLabel>
        </Price>
        <FeatureSection>
          {plan.features.map((f) => (
            <Feature feature={f} key={f.details} />
          ))}
        </FeatureSection>
      </div>
      <ButtonContainer>
        <CurrentPlanIndicator plan={plan} />
        <PlanActionButton interval={billingInterval} plan={plan} />
      </ButtonContainer>
    </Box>
  )
}

const formatTrialEndDate = (endDate: string) => {
  if (isPast(endDate)) {
    return ''
  }

  const days = getNumDays('', endDate)
  if (days < 1) {
    return 'Today'
  }
  if (days <= 30) {
    return `${days} days`
  }
  return formatDate(endDate, LONG_DATE_FORMAT)
}

const CurrentPlanIndicator = (props: {plan: PlanInfo}) => {
  const {plan} = props
  const subscription = useGetSubscription()
  const {isCurrent, isDowngradingTo} = usePlan()

  const renewDate = formatDate(subscription?.renews_at || '', LONG_DATE_FORMAT)
  const endDate = formatDate(subscription?.ends_at || '', LONG_DATE_FORMAT)
  const trialEndDate = formatTrialEndDate(subscription?.trial_ends_at || '')
  const isDowngrade = isDowngradingTo(plan, subscription)

  const paymentText = () => {
    const planPrice =
      subscription?.plan.interval === INTERVAL_MONTH
        ? plan.priceMonthly
        : plan.priceYearly

    return (
      <div>
        ${formatPrice(planPrice, {numDecimals: 0})} /{' '}
        {subscription?.plan.interval}
      </div>
    )
  }

  if (isDowngrade) {
    return (
      <TextButton>
        <div>Renewing On: {renewDate}</div>
        {paymentText()}
      </TextButton>
    )
  }

  if (!isCurrent(plan)) {
    return null
  }

  const dateText = () => {
    // If the renew_plan is this plan's name, we want to show the user that this
    // is the plan that is renewing on a date.
    if (subscription?.renew_plan === plan.name) {
      return <div>Renewing On: {renewDate}</div>
    }

    // If there is an ends_at date and we DON'T have a renew_plan value, this
    // subscription is being cancelled and we want to show WHEN it's ending.
    if (subscription?.ends_at && !subscription?.renew_plan) {
      return <div>Ending On: {endDate}</div>
    }

    if (subscription?.trial_ends_at) {
      return (
        <div>
          Free trial ends: {trialEndDate}
          <br />
          Next invoice on: {renewDate}
        </div>
      )
    }

    return <div>Next invoice on: {renewDate}</div>
  }

  return (
    <TextButton>
      <div aria-label={`current plan ${plan.name}`}>Your Current Plan</div>
      {dateText()}
      {paymentText()}
    </TextButton>
  )
}

function Feature(props: {feature: PlanInfo['features'][0]}) {
  const {feature} = props
  const iconColor = () => {
    if (!feature.isActive) {
      return '#A0A9B0'
    }

    return feature.isGood ? colors.success : colors.primary
  }

  const icon = feature.isGood ? (
    <CheckSquare color={iconColor()} iconSize={20} />
  ) : (
    <TimesSquare color={iconColor()} iconSize={20} />
  )

  return (
    <FeatureBox>
      <FeatureIcon>{icon}</FeatureIcon>
      <FeatureDetails>{feature.details}</FeatureDetails>
    </FeatureBox>
  )
}

const ButtonContainer = styled.div`
  align-items: flex-end;
  display: flex;
  flex-wrap: wrap;
`

const Box = styled.div`
  border: 1px solid ${(props) => props.theme.colors.border};
  padding: ${(props) => `${props.theme.spacing[6]} ${props.theme.spacing[10]}`};
  border-radius: 15px;
  height: 100%;
  display: flex;
  justify-content: space-between;
  flex-direction: column;
`

const Name = styled.h3`
  margin: 0 0 ${(props) => props.theme.spacing[4]};
  font-weight: normal;
`

const Price = styled.div`
  margin: 0 0 ${(props) => props.theme.spacing[8]};
  display: flex;
  align-items: flex-end;
`

const Dollars = styled.h2`
  font-family: Inter, sans-serif;
  font-style: normal;
  font-weight: 600;
  font-size: 36px;
  line-height: 48px;
  margin: 0;
`

const MonthLabel = styled.span`
  color: ${(props) => props.theme.colors.text.muted};
  font-weight: 300;
  margin: 0 0 4px;
`

const FeatureBox = styled.div`
  display: flex;
  margin-bottom: ${(props) => props.theme.spacing[3]};
`

const FeatureIcon = styled.div`
  margin-right: ${(props) => props.theme.spacing[3]};
`

const FeatureDetails = styled.span`
  font-family: Rubik;
  font-weight: 500;
  font-size: 16px;
  line-height: 24px;
`

const FeatureSection = styled.div`
  margin-bottom: ${(props) => props.theme.spacing[10]};
`

const TextButton = styled((props) => {
  return (
    <Button
      disableRipple
      disableFocusRipple
      fullWidth
      variant="text"
      {...props}
    />
  )
})`
  background-color: transparent !important;
  cursor: default;
  &:hover {
    background-color: transparent !important;
  }
  .MuiButton-label {
    flex-wrap: wrap;

    & > * {
      width: 100%;
    }
  }
`
